import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  List,
  ListItem,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Typography,
} from "@mui/material";
import {
  ArrowForwardIos as ArrowForwardIosIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";
import { collection, getDocs, query } from "firebase/firestore";
import { firestore } from "../../../firebase";
import { getScheduleDueStats, toDate } from "../../../utils/amcScheduleStats";

const isActiveSchedule = (schedule) => schedule?.IsActive !== false;
const normalizeValue = (value) => String(value || "").trim().toLowerCase();

const formatDate = (value) => {
  const dateValue = toDate(value);
  if (!dateValue) return "-";
  return dateValue.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const isScheduleAssignedToTechnician = (schedule, technician) => {
  const technicianValues = new Set(
    [
      technician.email,
      technician.id,
      technician.displayName,
      technician.adminName,
      technician.name,
    ]
      .map(normalizeValue)
      .filter(Boolean)
  );

  const topLevelValues = [
    schedule.assignedTechnicianEmail,
    schedule.assignedTechnicianId,
    schedule.assignedTechnicianName,
  ].map(normalizeValue);

  if (topLevelValues.some((value) => technicianValues.has(value))) {
    return true;
  }

  const dueRows = Array.isArray(schedule.DueDatesTable)
    ? schedule.DueDatesTable
    : [];

  return dueRows.some((row) =>
    [row.AssignedTo, row.AssignedToName, row.assignedTo, row.assignedToName]
      .map(normalizeValue)
      .some((value) => technicianValues.has(value))
  );
};

const TechnicianDashboard = () => {
  const navigate = useNavigate();
  const [assignedSchedules, setAssignedSchedules] = useState([]);
  const [showSchedules, setShowSchedules] = useState(false);
  const [scheduleStats, setScheduleStats] = useState({
    total: 0,
    active: 0,
    dueToday: 0,
    dueNextMonth: 0,
  });
  const [loading, setLoading] = useState(true);
  const [dateDialogOpen, setDateDialogOpen] = useState(false);
  const [selectedScheduleName, setSelectedScheduleName] = useState("");
  const [selectedScheduleDates, setSelectedScheduleDates] = useState([]);

  const profile = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("userProfile") || "{}");
    } catch {
      return {};
    }
  }, []);

  const technicianEmail = sessionStorage.getItem("userEmail") || profile.email || "";
  const technicianId = profile.id || "";
  const displayName =
    sessionStorage.getItem("userDisplayName") ||
    profile.displayName ||
    profile.adminName ||
    technicianEmail ||
    "Technician";

  useEffect(() => {
    let disposed = false;

    const loadAssignedSchedules = async () => {
      setLoading(true);
      try {
        const scheduleMap = new Map();
        const scheduleSnapshot = await getDocs(query(collection(firestore, "AMCScheduleMaster")));
        scheduleSnapshot.docs.forEach((entry) => {
          scheduleMap.set(entry.id, { id: entry.id, ...entry.data() });
        });

        if (disposed) return;

        const schedules = Array.from(scheduleMap.values()).filter((schedule) =>
          isScheduleAssignedToTechnician(schedule, {
            ...profile,
            email: technicianEmail,
            id: technicianId,
          })
        );
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        const todayEnd = new Date();
        todayEnd.setHours(23, 59, 59, 999);
        const nextMonthEnd = new Date(todayEnd);
        nextMonthEnd.setMonth(nextMonthEnd.getMonth() + 1);

        const { dueToday, dueNextMonth } = getScheduleDueStats(
          schedules,
          todayStart,
          todayEnd,
          nextMonthEnd
        );

        setAssignedSchedules(
          schedules.sort((a, b) => {
            const dateA = toDate(a.createdAt || a.AMCScheduleStartDate) || new Date(0);
            const dateB = toDate(b.createdAt || b.AMCScheduleStartDate) || new Date(0);
            return dateB - dateA;
          })
        );
        setScheduleStats({
          total: schedules.length,
          active: schedules.filter(isActiveSchedule).length,
          dueToday,
          dueNextMonth,
        });
      } catch (error) {
        console.error("Error loading technician dashboard:", error);
      } finally {
        if (!disposed) setLoading(false);
      }
    };

    loadAssignedSchedules();

    return () => {
      disposed = true;
    };
  }, [profile, technicianEmail, technicianId]);

  const ActionCard = ({ title, text, onOpen }) => (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        borderRadius: "12px",
      }}
    >
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {text}
        </Typography>
      </Box>
      <Button
        variant="contained"
        endIcon={<ArrowForwardIosIcon />}
        onClick={onOpen}
        sx={{ textTransform: "none", fontWeight: 700, whiteSpace: "nowrap" }}
      >
        Open
      </Button>
    </Paper>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Container maxWidth="xl" disableGutters>
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              fontFamily: "'Unbounded', cursive",
              fontWeight: 900,
              color: '#1c1400',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              lineHeight: 1,
            }}
          >
            Aalay<span style={{ color: '#f59e0b', fontSize: '1.8rem' }}>.</span>
          </Typography>

          <Typography
            variant="caption"
            component="div"
            sx={{
              fontStyle: 'italic',
              color: '#1c1400',
              opacity: 0.85,
              fontSize: '0.85rem',
              mt: 0.5,
              fontFamily: 'inherit',
              textTransform: 'none',
            }}
          >
            Intelligent Operations by OmneVu.com
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ color: "text.secondary", mb: 3 }}>
          Welcome, {displayName}
        </Typography>

        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} md={7}>
            <Card sx={{ p: { xs: 2, md: 3 } }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" component="h2">
                  Maintenance Schedule Management
                </Typography>
                <ScheduleIcon color="primary" />
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Odometer
                    label="Total assigned"
                    value={loading ? 0 : scheduleStats.total}
                    color="#1976d2"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Odometer
                    label="Active"
                    value={loading ? 0 : scheduleStats.active}
                    color="#388E3C"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Odometer
                    label="Due today"
                    value={loading ? 0 : scheduleStats.dueToday}
                    color="#D32F2F"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Odometer
                    label="Due next month"
                    value={loading ? 0 : scheduleStats.dueNextMonth}
                    color="#EF6C00"
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 2 }}>
                <ActionCard
                  title="Manage Maintenance Schedule"
                  text="View all maintenance schedules assigned to you."
                  onOpen={() => setShowSchedules(true)}
                />
              </Box>
            </Card>
          </Grid>
        </Grid>

        {showSchedules ? (
          <Paper sx={{ mt: 3, p: { xs: 2, md: 3 }, boxShadow: 3 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                mb: 2,
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Assigned Maintenance Schedules
              </Typography>
              
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold" }}>AMC Name</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Customer</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Customer Email</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Frequency</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Assigned By</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assignedSchedules.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>
                        No assigned maintenance schedules found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    assignedSchedules.map((schedule) => {
                      const dueDates = Array.isArray(schedule.DueDatesTable)
                        ? schedule.DueDatesTable
                        : [];
                      const formattedDates = dueDates
                        .map((row) => formatDate(row.DueDate))
                        .filter((date) => date !== "-");

                      return (
                        <TableRow key={schedule.id} hover>
                          <TableCell>{schedule.AMCName || "-"}</TableCell>
                          <TableCell>{schedule.CustomerName || "-"}</TableCell>
                          <TableCell>{schedule.CustomerEmailID || "-"}</TableCell>
                          <TableCell>
                            {formattedDates.length > 0 ? (
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                  setSelectedScheduleName(schedule.AMCName || "AMC Schedule");
                                  setSelectedScheduleDates(formattedDates);
                                  setDateDialogOpen(true);
                                }}
                                sx={{ textTransform: "none" }}
                              >
                                All Dates
                              </Button>
                            ) : (
                              formatDate(schedule.AMCScheduleStartDate)
                            )}
                          </TableCell>
                          <TableCell>
                            {[schedule.FreqOfMaintenance, schedule.UnitForDurationOfContract]
                              .filter(Boolean)
                              .join(" ") || "-"}
                          </TableCell>
                          <TableCell>
                            {schedule.assignedFacilityAdminName ||
                              schedule.assignedFacilityAdminEmail ||
                              "-"}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={isActiveSchedule(schedule) ? "Active" : "Inactive"}
                              color={isActiveSchedule(schedule) ? "success" : "default"}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ) : null}
      </Container>

      <Dialog
        open={dateDialogOpen}
        onClose={() => setDateDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{selectedScheduleName} - All Dates</DialogTitle>
        <DialogContent>
          {selectedScheduleDates.length > 0 ? (
            <List>
              {selectedScheduleDates.map((date, index) => (
                <ListItem key={`${date}-${index}`}>
                  <ListItemText primary={date} />
                </ListItem>
              ))}
            </List>
          ) : (
            <DialogContentText>No scheduled dates available.</DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDateDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const Odometer = ({ label, value, color }) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    let raf;
    const duration = 600;
    const start = performance.now();
    const initial = displayValue;
    const delta = value - initial;

    const animate = (time) => {
      const elapsed = time - start;
      const progress = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayValue(Math.round(initial + delta * eased));
      if (progress < 1) raf = requestAnimationFrame(animate);
    };

    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        backgroundColor: "#fafafa",
        borderRadius: "8px",
        height: "120px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
      }}
    >
      <Typography variant="body2" sx={{ color: "text.secondary", m: 0, fontSize: "0.875rem" }}>
        {label}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, m: 0, color, mt: 1 }}>
        {displayValue}
      </Typography>
    </Paper>
  );
};

export default TechnicianDashboard;
